import { makeAutoObservable, runInAction } from 'mobx'
import { isAxiosError } from 'axios'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { APP_ENV } from 'shared/config'
import { Phone } from 'entities/Phone/model/Phone'
import { PhoneApi } from 'entities/Phone'
import {
  type IParamsCreateNumbersLandline,
  type IResponseCreateNumbersLandlineError,
  type IResponseNumberLandline,
} from 'entities/Phone/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EnterNumberStore } from 'pages/settings/ui/EnterNumberCommon/store/EnterNumberStore'
import { EnterAddressStore } from 'pages/settings/ui/EnterAddressCommon/store/EnterAddressStore'
import { VerifyNumberStore } from 'pages/settings/ui/VerifyNumberCommon/store/VerifyNumberStore'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath'
import { AuthorizationDocumentStore } from 'pages/settings/ui/AuthorizationDocumentCommon/store/AuthorizationDocumentStore'
import { type NewInboxStepsStore } from './newInboxStepsStore'

export const numberLandlineStatus: IResponseNumberLandline['status'][] = [
  'twilio-processing',
  'received',
  'pending-verification',
  'verified',
  'pending-loa',
  'carrier-processing',
  'testing',
  'completed',
  'action-required',
  'failed',
  'canceled',
]

export class TextEnableLandlineStore {
  private readonly _enterNumberStore: EnterNumberStore
  private readonly _enterAddressStore: EnterAddressStore
  private readonly _verifyNumberStore: VerifyNumberStore
  private readonly _authorizationDocumentStore: AuthorizationDocumentStore

  private _phoneQuery: string | null = null
  private _phone: Phone | null = null
  private _cancelRequestLandline = new RequestCancelAxios()
  private _loading = true

  constructor(private _newInboxStepsStore: NewInboxStepsStore, private _entities: IEntitiesStore) {
    this._enterNumberStore = new EnterNumberStore({
      cancelRequestLandline: this._cancelRequestLandline,
      fetchNumbersLandline: this.fetchNumbersLandline,
    })
    this._enterAddressStore = new EnterAddressStore({
      fetchNumbersLandline: this.fetchNumbersLandline,
      createNumbersLandline: this.createNumbersLandline,
      getPhoneQuery: () => this.phoneQuery,
      nextStepByNumber: this.nextStepByNumber,
      nextStepNumber: 5,
    })
    this._verifyNumberStore = new VerifyNumberStore(
      {
        fetchNumbersLandline: this.fetchNumbersLandline,
        getPhone: () => this.phone,
        getPayload: () => this.payload,
        setPhone: this.setPhone,
        syncData: this.syncData,
        initStep: this.initStep,
      },
      this._entities
    )
    this._authorizationDocumentStore = new AuthorizationDocumentStore(
      {
        getPayload: () => this.payload,
        fetchNumbersLandline: this.fetchNumbersLandline,
        syncData: this.syncData,
        initStep: this.initStep,
        enterNumberStore: this._enterNumberStore,
      },
      this._entities
    )

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get cancelRequestLandline() {
    return this._cancelRequestLandline
  }

  get phone() {
    return this._phone
  }

  get authorizationDocumentStore() {
    return this._authorizationDocumentStore
  }

  get verifyNumberStore() {
    return this._verifyNumberStore
  }

  get enterNumberStore() {
    return this._enterNumberStore
  }

  get enterAddressStore() {
    return this._enterAddressStore
  }

  get payload(): IParamsCreateNumbersLandline {
    return {
      ...this._enterNumberStore.payload,
      ...this._enterAddressStore.payload,
      ...this._verifyNumberStore.payload,
      inbox_number: this._enterNumberStore.payload.phone,
      phone_to_replace: this._enterNumberStore.payload.phone,
    }
  }

  get phoneQuery() {
    return this._phoneQuery
  }

  setPhoneQuery = (value: string) => {
    this._phoneQuery = value
  }

  init = async (query: string) => {
    const urlSearchParams = new URLSearchParams(query)
    this._phoneQuery = urlSearchParams.get('phone')
    const statusQuery =
      APP_ENV === 'production'
        ? null
        : (urlSearchParams.get('status') as IResponseNumberLandline['status'] | null) // TODO: for testing

    if (this._phoneQuery) {
      this._enterNumberStore.setPhoneDisabled(true)
      this._enterNumberStore.setPhone(this._phoneQuery, false)

      const phoneInternational = this._enterNumberStore.phoneInternational
      const data = await this.fetchNumbersLandline(phoneInternational)

      if ('error' in data) {
        if (data.error === 'No such number') {
          uiStore.changeRouteViaVue({
            path: `/${InboxesPath.root}`,
          })
        }

        return
      } else if (numberLandlineStatus.includes(statusQuery)) {
        // TODO: For testing
        data.landline?.updateStatus(statusQuery)
      }

      if (this._phone) {
        const landline = this._phone.landline

        if (!landline || landline?.isCompleted) {
          uiStore.changeRouteViaVue({
            path: `/${InboxesPath.root}`,
          })

          return
        }

        this.syncData(this._phone)
        this.initStep(this._phone)
      }
    }

    runInAction(() => {
      this._loading = false
    })
  }

  syncData = (phone: Phone) => {
    this._phone = phone
    this._enterNumberStore.syncData(phone)
    this._enterAddressStore.syncData(phone)
    this._verifyNumberStore.syncData(phone)
  }

  initStep = (phone: Phone) => {
    const landline = phone?.landline

    if (!landline) return

    if (landline.statusTwilioProcessing) {
      this.nextStepByNumber(4)

      return
    }

    if (landline.hasVerify) {
      this.nextStepByNumber(5)

      return
    }

    if (landline.hasVerified || landline.hasLOA || landline.hasCompleted) {
      this.nextStepByNumber(6)
      this._authorizationDocumentStore.sync()

      return
    }
  }

  fetchNumbersLandline = async (number: string, update = true) => {
    try {
      this._cancelRequestLandline.init()

      const response = await PhoneApi.getNumbersLandline(
        {
          phone: number,
        },
        {
          cancelToken: this._cancelRequestLandline.token,
        }
      )

      const data = response.data

      if ('error' in data) {
        return {
          error: data.error,
        }
      } else {
        const phone = new Phone(data, this._entities)
        if (update) return this.setPhone(phone)
        return phone
      }
    } catch (e) {
      logger.error(e)
      return {
        error: 'unknow',
      }
    }
  }

  createNumbersLandline = async () => {
    try {
      const response = await PhoneApi.createNumbersLandline(this.payload)
      const data = response.data

      if ('error' in data) {
        return {
          error: data.error,
        }
      }

      const phone = new Phone(data, this._entities)
      this.setPhone(phone)
      this.setPhoneQuery(phone.national_number)

      return phone
    } catch (e) {
      const error = e as Error
      logger.error(error)

      if (isAxiosError<IResponseCreateNumbersLandlineError>(error)) {
        if (!error.response) {
          return {
            error: error.message,
          }
        }
        return {
          error: {
            ...error.response.data,
          },
        }
      }
      return {
        error: error.message,
      }
    }
  }

  dispose = () => {
    this._enterNumberStore.dispose()
    this._enterNumberStore.setPhone('')
    this._phone = null
    this._cancelRequestLandline.reset()
    this._enterAddressStore.dispose()
  }

  setPhone = (phone: Phone) => {
    this._phone = phone
    return this._phone
  }

  nextStepByNumber = (number: number, query?: string) => {
    this._newInboxStepsStore.nextStepByNumber(number, query)
  }
}
